// 网站配置
import { defineStore } from 'pinia'
import { config } from '@/config'
import { getDefaultLang, getTimeZone } from '@/utils/env'
import router from '@/router'

export const useAppStore = defineStore('app', {
  state: () => ({
    /** Logo组件显示文本 */
    logoLabel: config.logo.label,
    /**默认语言 */
    language: getDefaultLang(),
    /**时区*/
    timezone: getTimeZone(),
    /**国际化列表 */
    languages: [],
    /**内部-切换语言处理器*/
    _languageSwitchHandler: null,
    /**路由-当前页面名 */
    routerName: '',
    /**路由菜单-显示小红点的路由*/
    _routerRedDot: {},
    /** 导航栏菜单是否折叠 */
    menuIsCollapse: false,
    /** 是否启用暗黑模式 */
    themeDarkMode: false,
    /** 当前打开过的Tabs */
    openedTabs: [],
    /** 已展开的菜单分组列表 */
    expandedMenuGroups: [],
    /** 每页显示的数据*/
    pageSizes: [25, 50, 100, 250, 500],
    keepAliveMenus: [],
    /** 窗口宽度 */
    windowWidth: 0,
    /** 窗口高度 */
    windowHeight: 0,
    sounds: {
      // 新注册
      new_player: true,
      // 新充值
      new_deposit: true,
      // 新提现
      new_withdraw: true,
      // 新待转账
      new_transfer: true,
    },
    // 静音
    sounds_mute: false,
    // 网页是否点击过
    clickedWeb: false,
    /** 顶部数据 */
    topData: {
      pending_deposit_cnt: 0,
      pending_withdraw_cnt: 0,
      pending_transfer_cnt: 0,
      new_players_cnt: 0,
      online_players_cnt: 0,
      today_deposit_amount: 0,
      today_withdraw_amount: 0,
      pending_agent_unhandled_cnt: 0,
      bi_wait_cnt: 0,
    },
  }),

  getters: {},

  /*****************************************
   * 自定义函数
   *****************************************/
  actions: {
    /**
     * 更新支持的国际化语言列表
     * @param configs
     * @param languageSwitchHandler 切换语言的处理逻辑
     */
    initLanguage(configs, languageSwitchHandler) {
      this._languageSwitchHandler = languageSwitchHandler
      this.languages.splice(0, this.languages.length)
      for (const k in configs) {
        this.languages.push(k)
      }

      this.setLanguage(this.language)
    },
    /**
     * 设置语言
     */
    setLanguage(lang) {
      if (lang && lang.value) lang = lang.value
      if (!this._languageSwitchHandler) return false
      if (!this._languageSwitchHandler(lang)) return false
      this.language = lang
      return true
    },
    /**
     * 设置路由小红点
     * @param path 路由路径
     * @param content 显示内容，如果内容为空，显示单纯的小红点
     * @param clickHide 点击后隐藏，默认为true
     */
    setRouteRedDot(path, content = '', clickHide = true) {
      const hasContent =
        content !== undefined &&
        content !== null &&
        content !== 0 &&
        content !== '0' &&
        content !== '' &&
        content !== '0/0' &&
        content !== '0 / 0'
      const currentRoute = this._routerRedDot[path]
      const isCurrentPage = router.currentRoute.value.path === path

      // 如果没有内容，直接设置为不显示
      if (!hasContent) {
        this._routerRedDot[path] = {
          display: false,
          content: '',
          clickHide,
          looked: false,
        }
        return
      }

      // 检查内容是否发生变化
      const contentChanged = currentRoute?.content !== content

      // 确定是否应该显示小红点
      // 如果是当前页面，不显示小红点；否则根据内容变化或上次状态决定
      const shouldDisplay = contentChanged || (currentRoute?.display ?? true)
      let isLooked = this._routerRedDot[path]?.looked
      if (isCurrentPage) {
        isLooked = true
      } else if (contentChanged) {
        isLooked = false
      }

      // 设置路由红点状态
      this._routerRedDot[path] = {
        display: shouldDisplay,
        content,
        clickHide,
        looked: isLooked || false, // 当前页面或内容未变化时标记为已查看
      }
      // console.error(path, this._routerRedDot[path])
    },
    /**
     * 取路由是否显示小红点
     * @param path 路由路径
     * @returns {boolean}
     */
    isShowRouteRedDot(path) {
      const route = this._routerRedDot[path]
      return route && route.display
    },
    /**
     * 取油路显示内容
     * @param path
     * @returns {String} 返回内容, 如果为空显示小红点
     */
    getRouteRedDotValue(path) {
      const route = this._routerRedDot[path]
      return route && route.content
    },
    /**
     * 取路由是否已经查看过小红点
     * @param path 路径
     * @returns {boolean}
     */
    getRouteRedDotIsLooked(path) {
      const route = this._routerRedDot[path]
      return route && route.looked
    },
    /**
     * 尝试隐藏小红点
     * @param path 路由路径
     */
    tryHideRouteRedDot(path) {
      const route = this._routerRedDot[path]
      if (!route) {
        return
      }
      if (route.clickHide) {
        this._routerRedDot[path].display = false
      }
      this._routerRedDot[path].looked = true
    },

    /**
     * 切换菜单分组的展开/收缩状态
     * @param {string} groupName 菜单分组名称
     */
    toggleMenuGroup(groupName) {
      const index = this.expandedMenuGroups.indexOf(groupName)
      if (index > -1) {
        // 当前是展开状态，收缩它
        this.expandedMenuGroups.splice(index, 1)
      } else {
        // 当前是收缩状态，展开它
        this.expandedMenuGroups.push(groupName)
      }
    },

    /**
     * 检查菜单分组是否已展开
     * @param {string} groupName 菜单分组名称
     * @returns {boolean} true=展开，false=收缩
     */
    isMenuGroupExpanded(groupName) {
      return this.expandedMenuGroups.includes(groupName)
    },

    /**
     * 手动设置菜单分组状态
     * @param {string} groupName 菜单分组名称
     * @param {boolean} expanded true=展开，false=收缩
     */
    setMenuGroupState(groupName, expanded) {
      const index = this.expandedMenuGroups.indexOf(groupName)
      if (expanded && index === -1) {
        // 要展开，但不在展开列表中，添加它
        this.expandedMenuGroups.push(groupName)
      } else if (!expanded && index > -1) {
        // 要收缩，但在展开列表中，移除它
        this.expandedMenuGroups.splice(index, 1)
      }
    },
  },

  /*****************************************
   * 持久化数据配置
   *****************************************/
  persist: {
    storage: localStorage,
    pick: [
      'language',
      'timezone',
      'routerName',
      'routerPath',
      'themeDarkMode',
      'openedTabs',
      'expandedMenuGroups',
      'pageNum_userList',
      'sounds',
      'sounds_mute',
    ],
  },
})
